import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TourGuide from './TourGuide';
import { getTourForPath } from './tourSteps';
import { endDemoSession, isDemoMode, isTourSeen, markTourSeen } from './demoMode';

/** 마법사처럼 한 주소 안에서 화면이 바뀌는 경우를 위한 표식 읽기 */
function readScreenKey() {
  try {
    const el = document.querySelector('[data-tour-screen]');
    return el ? String(el.getAttribute('data-tour-screen') || '') : '';
  } catch (_) {
    return '';
  }
}

/**
 * 데모 모드에서만 보이는 안내 레이어.
 *  - 화면 왼쪽 아래에 "데모 버전" 배지를 띄운다.
 *  - 튜토리얼 대본이 있는 화면에 처음 들어오면 자동으로 말풍선을 시작한다.
 *  - 배지의 [튜토리얼 보기] 로 언제든 다시 볼 수 있다.
 *
 * Layout 안에 한 번만 올려 두면 된다.
 */
const DemoOverlay = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tourOpen, setTourOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [screenKey, setScreenKey] = useState('');

  const demo = isDemoMode();

  // data-tour-screen 이 바뀌면(마법사 단계 이동 등) 대본도 따라 바뀌어야 한다.
  useEffect(() => {
    if (!demo) return undefined;
    const read = () => setScreenKey(readScreenKey());
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-tour-screen'],
    });
    return () => observer.disconnect();
  }, [demo, location.pathname]);

  const tour = getTourForPath(location.pathname, screenKey);

  // 대본이 있는 화면에 처음 들어오면 자동 시작.
  // 화면이 그려지고 데이터가 오는 시간을 조금 기다렸다가 띄운다.
  const tourKey = tour ? tour.key : '';
  useEffect(() => {
    // 다른 화면(또는 마법사 다음 단계)으로 넘어갔으면 이전 말풍선은 먼저 닫는다.
    setTourOpen(false);
    if (!demo || !tourKey || isTourSeen(tourKey)) return undefined;

    const timer = setTimeout(() => setTourOpen(true), 700);
    return () => clearTimeout(timer);
  }, [demo, tourKey]);

  const handleClose = useCallback(() => {
    if (tour) markTourSeen(tour.key);
    setTourOpen(false);
  }, [tour]);

  const handleExit = useCallback(() => {
    endDemoSession();
    navigate('/login', { replace: true });
    // 인터셉터가 물고 있던 상태를 완전히 털어내기 위해 새로 띄운다.
    setTimeout(() => window.location.reload(), 0);
  }, [navigate]);

  if (!demo) return null;

  return (
    <>
      <TourGuide
        key={tourKey || 'none'}
        steps={tour ? tour.steps : []}
        open={tourOpen && !!tour}
        onClose={handleClose}
      />

      <div className="fixed bottom-4 left-4 z-[99999] print:hidden">
        {collapsed ? (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white shadow-lg transition-colors hover:bg-indigo-700"
            title="데모 안내 열기"
          >
            데모
          </button>
        ) : (
          <div className="flex items-center gap-2 rounded-full bg-[#141618]/95 py-1.5 pl-3 pr-1.5 shadow-xl ring-1 ring-white/10 backdrop-blur">
            {/* 다크모드와 무관하게 항상 어두운 띠: 색 변수 대신 고정값 */}
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-white">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
              데모 버전
            </span>
            <span className="text-[11px] text-[#a0a4ab]">샘플 데이터 · 저장되지 않습니다</span>

            <button
              type="button"
              onClick={() => setTourOpen(true)}
              disabled={!tour}
              className="rounded-full bg-indigo-600 px-3 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-[#43474d] disabled:text-[#a0a4ab]"
              title={tour ? `${tour.label} 튜토리얼 보기` : '이 화면에는 준비된 튜토리얼이 없습니다'}
            >
              튜토리얼 보기
            </button>

            <button
              type="button"
              onClick={handleExit}
              className="rounded-full px-2.5 py-1 text-[12px] font-medium text-[#dcdee2] transition-colors hover:bg-white/10 hover:text-white"
            >
              나가기
            </button>

            <button
              type="button"
              onClick={() => setCollapsed(true)}
              aria-label="데모 안내 접기"
              className="rounded-full p-1 text-[#a0a4ab] transition-colors hover:bg-white/10 hover:text-white"
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default DemoOverlay;
